/**
 * ScannerTreeBuilder — recursive AND/OR trigger-tree editor for the scanner DSL.
 *
 * The core `CriteriaGroupBuilder` was evaluated for reuse but it targets a
 * different model: a flat `CriteriaGroup[]` keyed on `operator`, numeric-only
 * inputs, and the entry-signal field list — it cannot express the scanner DSL's
 * recursive `group`/`condition` node tree, string operators (eq_str/contains/
 * in_list), cross operators, `value_str`, or `negate`. This builder keeps the same
 * visual language (AND/OR toggle chips, bordered group cards, condition rows) but
 * emits/consumes the Lab's `ScanGroupNode` tree directly.
 */

import React from 'react';
import { Trash, Plus, FolderPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from '../ui/select';
import type { ScanGroupNode, ScanConditionNode, ScannerOp } from '../../types/strategyLab';
import {
  SCANNER_FIELDS,
  SCANNER_FIELD_MAP,
  SCANNER_OP_MAP,
  operatorsForFieldType,
  defaultOpForFieldType,
  type ScannerFieldDef,
} from './scannerFields';

// ── Node factories ──
export function newCondition(): ScanConditionNode {
  return { type: 'condition', field: '', op: 'gt', value: 0 };
}
export function newGroup(logic: 'and' | 'or' = 'and'): ScanGroupNode {
  return { type: 'group', logic, children: [newCondition()] };
}

// Fields grouped by category for the picker.
const FIELDS_BY_CATEGORY: Array<{ category: string; fields: ScannerFieldDef[] }> = (() => {
  const order: string[] = [];
  const byCat: Record<string, ScannerFieldDef[]> = {};
  for (const f of SCANNER_FIELDS) {
    if (!byCat[f.category]) {
      byCat[f.category] = [];
      order.push(f.category);
    }
    byCat[f.category].push(f);
  }
  return order.map((category) => ({ category, fields: byCat[category] }));
})();

interface ConditionEditorProps {
  node: ScanConditionNode;
  logicLabel: string;
  showLogicLabel: boolean;
  canRemove: boolean;
  onChange: (node: ScanConditionNode) => void;
  onRemove: () => void;
}

const ConditionEditor: React.FC<ConditionEditorProps> = ({
  node,
  logicLabel,
  showLogicLabel,
  canRemove,
  onChange,
  onRemove,
}) => {
  const fieldDef = node.field ? SCANNER_FIELD_MAP[node.field] : undefined;
  const fieldType = fieldDef?.type ?? 'number';
  const opDef = SCANNER_OP_MAP[node.op];
  const opKind = opDef?.kind ?? 'numeric';
  const validOps = operatorsForFieldType(fieldType);

  const handleFieldChange = (fieldKey: string) => {
    const def = SCANNER_FIELD_MAP[fieldKey];
    const nextType = def?.type ?? 'number';
    // Keep the current op only if it's still valid for the new field type.
    const stillValid = operatorsForFieldType(nextType).some((o) => o.value === node.op);
    const nextOp: ScannerOp = stillValid ? node.op : defaultOpForFieldType(nextType);
    onChange({
      ...node,
      field: fieldKey,
      op: nextOp,
      value: nextType === 'string' ? null : (node.value ?? 0),
      value2: nextOp === 'between' ? (node.value2 ?? 0) : null,
      value_str: nextType === 'string' ? (node.value_str ?? '') : null,
    });
  };

  const handleOpChange = (op: string) => {
    const nextOp = op as ScannerOp;
    const nextKind = SCANNER_OP_MAP[nextOp]?.kind ?? 'numeric';
    onChange({
      ...node,
      op: nextOp,
      value2: nextKind === 'range' ? (node.value2 ?? 0) : null,
      value: nextKind === 'string' ? null : (node.value ?? 0),
      value_str: nextKind === 'string' ? (node.value_str ?? '') : null,
    });
  };

  const numberValue = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : String(v));

  return (
    <div>
      {showLogicLabel && (
        <Badge variant="outline" className="text-[10px] my-2 block w-fit mx-auto">
          {logicLabel}
        </Badge>
      )}
      <div className="flex flex-wrap items-end gap-2">
        {/* Field */}
        <div className="flex-1 min-w-[150px]">
          <Label className="text-xs text-muted-foreground mb-1 block">Field</Label>
          <Select value={node.field} onValueChange={handleFieldChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select field" />
            </SelectTrigger>
            <SelectContent>
              {FIELDS_BY_CATEGORY.map(({ category, fields }) => (
                <SelectGroup key={category}>
                  <SelectLabel>{category}</SelectLabel>
                  {fields.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Operator */}
        <div className="w-[140px]">
          <Label className="text-xs text-muted-foreground mb-1 block">Operator</Label>
          <Select value={node.op} onValueChange={handleOpChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {validOps.map((op) => (
                <SelectItem key={op.value} value={op.value}>
                  {op.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Value(s) */}
        {opKind === 'string' ? (
          <div className="w-[180px]">
            <Label className="text-xs text-muted-foreground mb-1 block">
              {node.op === 'in_list' ? 'Values (comma-sep)' : 'Text'}
            </Label>
            <Input
              type="text"
              value={node.value_str ?? ''}
              placeholder={node.op === 'in_list' ? 'AAPL, MSFT, TSLA' : 'Technology'}
              onChange={(e) => onChange({ ...node, value_str: e.target.value })}
            />
          </div>
        ) : (
          <div className="w-[110px]">
            <Label className="text-xs text-muted-foreground mb-1 block">
              {opKind === 'range' ? 'Min' : 'Value'}
              {fieldDef?.unit ? ` (${fieldDef.unit})` : ''}
            </Label>
            <Input
              type="number"
              value={numberValue(node.value)}
              onChange={(e) =>
                onChange({ ...node, value: e.target.value === '' ? null : Number(e.target.value) })
              }
            />
          </div>
        )}

        {opKind === 'range' && (
          <div className="w-[110px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Max</Label>
            <Input
              type="number"
              value={numberValue(node.value2)}
              onChange={(e) =>
                onChange({ ...node, value2: e.target.value === '' ? null : Number(e.target.value) })
              }
            />
          </div>
        )}

        {/* NOT toggle */}
        <div className="flex flex-col items-center gap-1">
          <Label className="text-xs text-muted-foreground mb-0.5">NOT</Label>
          <Switch
            checked={!!node.negate}
            onCheckedChange={(checked) => onChange({ ...node, negate: checked })}
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-rose-500 hover:text-rose-600"
          title="Remove condition"
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

interface GroupEditorProps {
  node: ScanGroupNode;
  depth: number;
  isRoot: boolean;
  onChange: (node: ScanGroupNode) => void;
  onRemove?: () => void;
}

const GroupEditor: React.FC<GroupEditorProps> = ({ node, depth, isRoot, onChange, onRemove }) => {
  const setLogic = (logic: 'and' | 'or') => onChange({ ...node, logic });

  const updateChild = (idx: number, child: ScanGroupNode | ScanConditionNode) => {
    const children = [...node.children];
    children[idx] = child;
    onChange({ ...node, children });
  };

  const removeChild = (idx: number) => {
    const children = node.children.filter((_, i) => i !== idx);
    onChange({ ...node, children });
  };

  const addCondition = () => onChange({ ...node, children: [...node.children, newCondition()] });
  const addGroup = () => onChange({ ...node, children: [...node.children, newGroup(node.logic === 'and' ? 'or' : 'and')] });

  const onlyChild = node.children.length <= 1;

  return (
    <div className={`border rounded-lg p-4 space-y-3 ${isRoot ? 'bg-card border-border' : 'bg-muted/30 border-border/70'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-foreground">
            {isRoot ? 'Match' : 'Group'}
          </span>
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button
              type="button"
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                node.logic === 'and'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => setLogic('and')}
            >
              ALL (AND)
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                node.logic === 'or'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => setLogic('or')}
            >
              ANY (OR)
            </button>
          </div>
        </div>
        {!isRoot && onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="text-rose-500 hover:text-rose-600"
            title="Remove group"
          >
            <Trash className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-1">
        {node.children.map((child, idx) => (
          <div key={idx}>
            {idx > 0 && child.type === 'group' && (
              <div className="flex items-center gap-3 my-3">
                <Separator className="flex-1" />
                <Badge variant="secondary" className="text-[10px] font-bold">
                  {node.logic.toUpperCase()}
                </Badge>
                <Separator className="flex-1" />
              </div>
            )}
            {child.type === 'condition' ? (
              <ConditionEditor
                node={child}
                logicLabel={node.logic.toUpperCase()}
                showLogicLabel={idx > 0}
                canRemove={!(isRoot && onlyChild)}
                onChange={(c) => updateChild(idx, c)}
                onRemove={() => removeChild(idx)}
              />
            ) : (
              <GroupEditor
                node={child}
                depth={depth + 1}
                isRoot={false}
                onChange={(c) => updateChild(idx, c)}
                onRemove={() => removeChild(idx)}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={addCondition} variant="outline" size="sm" className="gap-2">
          <Plus className="h-4 w-4" /> Add condition
        </Button>
        {depth < 2 && (
          <Button onClick={addGroup} variant="outline" size="sm" className="gap-2">
            <FolderPlus className="h-4 w-4" /> Add nested group
          </Button>
        )}
      </div>
    </div>
  );
};

interface ScannerTreeBuilderProps {
  root: ScanGroupNode;
  onChange: (root: ScanGroupNode) => void;
}

const ScannerTreeBuilder: React.FC<ScannerTreeBuilderProps> = ({ root, onChange }) => {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Build the trigger tree from day-trading data points. Toggle each group between
        ALL (AND) and ANY (OR); nest groups for mixed logic. Symbols passing the top-level
        match are surfaced by the scanner.
      </p>
      <GroupEditor node={root} depth={0} isRoot onChange={onChange} />
    </div>
  );
};

export default ScannerTreeBuilder;
